import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import {
  ANSWER_TYPES, CRITICAL_SCOPES, hasOptions, isScored, REQUIREMENT_TRIGGERS,
  type AnswerType, type CriticalScope, type LibraryQuestion,
  type QuestionCategory, type RequirementTrigger, type SaveLibraryQuestion,
} from '../api/types';
import {
  IconArrowRight,
  IconCircleAlert,
  IconInbox,
  IconPencil,
  IconPlus,
  IconPower,
  IconRefreshCw,
  IconSearchX,
  IconTrash2,
  IconTriangleAlert,
} from '../icons';
import { CODE_MAX, codeUpper, compare, tn } from '../i18n';
import { describeError, useTranslate } from '../i18n/react';
import { Badge } from '../ui/Badge';
import { Button, IconButton } from '../ui/Button';
import { AnswerTypeBadge, BehaviourBadges } from '../ui/codes';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Drawer } from '../ui/Drawer';
import { EmptyState } from '../ui/EmptyState';
import { Alert, Field, SearchInput, Select, Textarea, TextInput, Toggle } from '../ui/Field';
import { Card, PageHeader, Toolbar } from '../ui/Page';
import { RowMenu } from '../ui/RowMenu';
import { Table, TableFooter, type Column, type SortState } from '../ui/Table';

const EMPTY: SaveLibraryQuestion = {
  categoryId: '',
  code: '',
  text: '',
  guidance: null,
  answerType: 'YesNo',
  scaleMaximum: 5,
  options: [],
  criticalScope: 'None',
  commentRequirement: 'Never',
  photoRequirement: 'Never',
  allowsNotApplicable: false,
};

/** How the sortable columns read a row. Sorting is done here, not on the server. */
const SORT_KEYS: Record<string, (row: LibraryQuestion) => string> = {
  category: (row) => row.categoryName,
  code: (row) => row.code,
  text: (row) => row.text,
};

/**
 * The question library — the screen the whole product is built out of.
 *
 * <p>Search and the category filter live in the URL, so a filtered list can be refreshed,
 * bookmarked and sent to a colleague. The search goes to the server, which is why it is
 * debounced: the old version fired a request per keystroke.</p>
 *
 * <p>Sorting is done here with a Turkish collator. The server returns a sensible order, but
 * "Çatı" and "Isıtma" land in the wrong place under a default string comparison, which is
 * the sort of thing that makes a list feel broken without anyone being able to say why.</p>
 */
export function Questions() {
  const t = useTranslate();
  const [params, setParams] = useSearchParams();
  const categoryFilter = params.get('kategori') ?? '';
  const term = params.get('ara') ?? '';

  const [categories, setCategories] = useState<QuestionCategory[]>([]);
  const [rows, setRows] = useState<LibraryQuestion[]>([]);
  const [typed, setTyped] = useState(term);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState | null>({ columnId: 'code', direction: 'asc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [doomed, setDoomed] = useState<LibraryQuestion | null>(null);

  const [editing, setEditing] = useState<{ id: string | null; body: SaveLibraryQuestion } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // Typing goes into the URL after a pause. Immediately would mean a request per keystroke;
  // the filter still has to survive a refresh, so it cannot stay in component state either.
  useEffect(() => {
    if (typed === term) return;

    const timer = window.setTimeout(() => {
      const next = new URLSearchParams(params);
      if (typed) next.set('ara', typed);
      else next.delete('ara');
      setParams(next, { replace: true });
    }, 300);

    return () => window.clearTimeout(timer);
  }, [typed, term, params, setParams]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [list, cats] = await Promise.all([
        api.questions.list(categoryFilter || undefined, term || undefined),
        api.categories.list(),
      ]);
      setRows(list);
      setCategories(cats);
      setFailed(false);
      setError(null);
    } catch (e) {
      setFailed(true);
      setError(describeError(e, 'error.listFailed'));
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, term]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // A narrowed list is a shorter list; staying on page four of it shows nothing.
  useEffect(() => setPage(1), [categoryFilter, term, pageSize]);

  const sorted = useMemo(() => {
    const read = sort ? SORT_KEYS[sort.columnId] : undefined;
    if (!read) return rows;

    const direction = sort?.direction === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => direction * compare(read(a), read(b)));
  }, [rows, sort]);

  const shown = sorted.slice((page - 1) * pageSize, page * pageSize);

  function setCategory(next: string) {
    const params2 = new URLSearchParams(params);
    if (next) params2.set('kategori', next);
    else params2.delete('kategori');
    setParams(params2);
  }

  async function openNew() {
    const categoryId = categoryFilter || categories[0]?.id || '';
    const body = { ...EMPTY, categoryId };

    if (categoryId) {
      // The next free code under the category. Odito leaves this to whoever is typing,
      // which is how its own screens ended up with D-1, D-2, D-4.
      try {
        body.code = (await api.categories.nextQuestionCode(categoryId)).code;
      } catch {
        // A suggestion that cannot be fetched is not a reason to block the form.
      }
    }

    setFormError(null);
    setEditing({ id: null, body });
  }

  function openExisting(row: LibraryQuestion) {
    setFormError(null);
    setEditing({
      id: row.id,
      body: {
        categoryId: row.categoryId,
        code: row.code,
        text: row.text,
        guidance: row.guidance,
        answerType: row.answerType,
        scaleMaximum: row.scaleMaximum,
        options: row.options.map((o) => ({ text: o.text, score: o.score })),
        criticalScope: row.criticalScope,
        commentRequirement: row.commentRequirement,
        photoRequirement: row.photoRequirement,
        allowsNotApplicable: row.allowsNotApplicable,
      },
    });
  }

  /** Copies a question into the form unsaved, so the code and text can be changed first. */
  function duplicate(row: LibraryQuestion) {
    openExisting(row);
    setEditing((previous) =>
      previous ? { id: null, body: { ...previous.body, code: `${previous.body.code}-2` } } : previous,
    );
  }

  async function save() {
    if (!editing) return;

    try {
      if (editing.id) await api.questions.update(editing.id, editing.body);
      else await api.questions.create(editing.body);
      setEditing(null);
      await reload();
    } catch (e) {
      setFormError(describeError(e, 'error.saveFailed'));
    }
  }

  async function act(run: () => Promise<void>) {
    try {
      await run();
      setError(null);
      await reload();
    } catch (e) {
      setError(describeError(e, 'error.actionFailed'));
    }
  }

  const body = editing?.body;
  const criticalAndSkippable = !!body && body.criticalScope !== 'None' && body.allowsNotApplicable;
  const canSave = !!body && body.text.trim() !== '' && body.code.trim() !== '' && !criticalAndSkippable;

  function patch(change: Partial<SaveLibraryQuestion>) {
    if (!editing) return;
    setEditing({ ...editing, body: { ...editing.body, ...change } });
  }

  const columns: Column<LibraryQuestion>[] = [
    {
      id: 'category',
      header: t('questions.category'),
      cell: (row) => row.categoryName,
      className: 'is-muted',
      width: '13%',
      sortable: true,
      skeletonWidth: '70%',
    },
    {
      id: 'code',
      header: t('questions.code'),
      cell: (row) => <span className="nv-code">{row.code}</span>,
      width: '9%',
      sortable: true,
      skeletonWidth: '52px',
    },
    {
      id: 'text',
      header: t('questions.text'),
      // The widest column, in the main text colour: it is what the screen is about.
      cell: (row) => row.text,
      className: 'is-primary',
      sortable: true,
      skeletonWidth: '88%',
    },
    {
      id: 'type',
      header: t('questions.type'),
      width: '12%',
      cell: (row) => (
        <span className="nv-badge-group">
          <AnswerTypeBadge type={row.answerType} />
          {row.answerType === 'Scale' ? <span className="nv-subtle">1–{row.scaleMaximum}</span> : null}
          {hasOptions(row.answerType) ? (
            <span className="nv-subtle">{t('questions.optionCount', { count: row.options.length })}</span>
          ) : null}
        </span>
      ),
      skeletonWidth: '76px',
    },
    {
      id: 'behaviour',
      header: t('questions.behaviour'),
      width: '18%',
      cell: (row) => (
        <span className="nv-badge-group">
          <BehaviourBadges question={row} />
          {row.isActive ? null : <Badge tone="neutral">{t('questions.retired')}</Badge>}
        </span>
      ),
      skeletonWidth: '84%',
    },
    {
      id: 'usage',
      header: t('questions.usage'),
      width: '10%',
      cell: (row) =>
        row.usedInTemplates > 0 ? (
          <Link className="nv-usage" to={`/sablonlar?soru=${row.id}`}>
            {t('questions.usedIn', { count: row.usedInTemplates })}
            <IconArrowRight size={13} />
          </Link>
        ) : (
          <span className="nv-subtle">{t('questions.unused')}</span>
        ),
      skeletonWidth: '60px',
    },
  ];

  return (
    <>
      <PageHeader
        crumbs={[t('app.definitions'), t('nav.questions')]}
        title={t('questions.title')}
        subtitle={t('questions.subtitle', { count: rows.length })}
        action={
          <Button
            variant="primary"
            icon={IconPlus}
            disabled={categories.length === 0}
            title={categories.length === 0 ? t('questions.needCategory') : undefined}
            onClick={() => void openNew()}
          >
            {t('questions.add')}
          </Button>
        }
      />

      {error ? <Alert>{error}</Alert> : null}
      {categories.length === 0 && !loading ? <Alert tone="warning">{t('questions.needCategory')}</Alert> : null}

      <Card className="nv-table-card">
        <Toolbar count={tn('common.shown', rows.length)}>
          <SearchInput value={typed} onValueChange={setTyped} placeholder={t('questions.search')} />

          <Select
            aria-label={t('questions.category')}
            value={categoryFilter}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">{t('questions.allCategories')}</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </Select>

          {categoryFilter || term ? (
            <Button variant="ghost" size="sm" onClick={() => setParams({})}>{t('common.clearFilters')}</Button>
          ) : null}
        </Toolbar>

        <Table
          caption={t('questions.title')}
          columns={columns}
          rows={shown}
          getRowId={(row) => row.id}
          status={loading ? 'loading' : failed ? 'error' : 'ready'}
          sort={sort}
          onSortChange={setSort}
          onRowClick={openExisting}
          rowClassName={(row) => (row.isActive ? undefined : 'is-retired')}
          rowActions={(row) => (
            <>
              <IconButton label={t('common.edit')} icon={IconPencil} onClick={() => openExisting(row)} />
              <RowMenu
                label={t('common.moreActions')}
                items={[
                  { label: t('common.copy'), icon: IconPlus, onSelect: () => duplicate(row) },
                  {
                    label: row.isActive ? t('common.retire') : t('common.reinstate'),
                    icon: row.isActive ? IconPower : IconRefreshCw,
                    onSelect: () =>
                      void act(() => (row.isActive ? api.questions.retire(row.id) : api.questions.reinstate(row.id))),
                  },
                  { label: t('common.delete'), icon: IconTrash2, danger: true, onSelect: () => setDoomed(row) },
                ]}
              />
            </>
          )}
          errorState={
            <EmptyState
              icon={IconCircleAlert}
              tone="danger"
              title={t('common.loadFailed')}
              text={t('common.loadFailedText')}
              action={<Button icon={IconRefreshCw} onClick={() => void reload()}>{t('common.retry')}</Button>}
            />
          }
          emptyState={
            term || categoryFilter ? (
              <EmptyState
                icon={IconSearchX}
                title={t('common.noResults', { term: term || t('common.all') })}
                text={t('common.noResultsText')}
                action={<Button onClick={() => setParams({})}>{t('common.clearFilters')}</Button>}
              />
            ) : (
              <EmptyState
                icon={IconInbox}
                title={t('questions.empty')}
                text={t('questions.emptyText')}
                action={
                  categories.length === 0 ? undefined : (
                    <Button variant="primary" icon={IconPlus} onClick={() => void openNew()}>
                      {t('questions.add')}
                    </Button>
                  )
                }
              />
            )
          }
        />

        <TableFooter
          total={tn('common.total', rows.length)}
          page={page}
          pageSize={pageSize}
          rowCount={rows.length}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Card>

      <Drawer
        open={editing !== null}
        title={t(editing?.id ? 'questions.editTitle' : 'questions.newTitle')}
        onClose={() => setEditing(null)}
        footer={
          <>
            <Button onClick={() => setEditing(null)}>{t('common.cancel')}</Button>
            <Button variant="primary" disabled={!canSave} onClick={() => void save()}>{t('common.save')}</Button>
          </>
        }
      >
        {body && editing ? (
          <>
            <Field label={t('questions.category')}>
              {({ id }) => (
                <Select
                  id={id}
                  value={body.categoryId}
                  onChange={(e) => {
                    const categoryId = e.target.value;
                    patch({ categoryId });

                    if (!editing.id) {
                      void api.categories
                        .nextQuestionCode(categoryId)
                        .then((r) => patch({ categoryId, code: r.code }))
                        .catch(() => undefined);
                    }
                  }}
                >
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </Select>
              )}
            </Field>

            <div className="nv-form-pair">
              <Field label={t('questions.code')} hint={editing.id ? undefined : t('questions.codeHint')}>
                {({ id, describedBy }) => (
                  <TextInput
                    id={id}
                    aria-describedby={describedBy}
                    maxLength={CODE_MAX}
                    value={body.code}
                    onChange={(e) => patch({ code: codeUpper(e.target.value) })}
                  />
                )}
              </Field>

              <Field label={t('questions.type')}>
                {({ id }) => (
                  <Select
                    id={id}
                    value={body.answerType}
                    onChange={(e) => {
                      const next = e.target.value as AnswerType;
                      patch({
                        answerType: next,
                        // Dropped here as well as on the server. A list that reappears when
                        // somebody changes the type back reappears stale.
                        options: hasOptions(next) ? body.options : [],
                        // Free text never scores, so it can never be critical either.
                        criticalScope: isScored(next) ? body.criticalScope : 'None',
                        // Nor negative: "when negative" asks for a photo on an answer text never gives.
                        commentRequirement: !isScored(next) && body.commentRequirement === 'WhenNegative' ? 'Never' : body.commentRequirement,
                        photoRequirement: !isScored(next) && body.photoRequirement === 'WhenNegative' ? 'Never' : body.photoRequirement,
                      });
                    }}
                  >
                    {ANSWER_TYPES.map((value) => (
                      <option key={value} value={value}>{t(`answerType.${value}`)}</option>
                    ))}
                  </Select>
                )}
              </Field>
            </div>

            {body.answerType === 'Scale' ? (
              <Field label={t('questions.scaleMaximum')} hint={t('questions.scaleHint', { max: body.scaleMaximum })}>
                {({ id, describedBy }) => (
                  <TextInput
                    id={id}
                    aria-describedby={describedBy}
                    type="number"
                    min={2}
                    max={10}
                    value={body.scaleMaximum}
                    onChange={(e) => patch({ scaleMaximum: Number(e.target.value) })}
                  />
                )}
              </Field>
            ) : null}

            {hasOptions(body.answerType) ? (
              <div className="nv-drawer__section">
                <h3 className="nv-h3">{t('questions.options')}</h3>
                <p className="nv-hint">
                  {t(
                    body.answerType === 'SingleChoice'
                      ? 'questions.optionsSingleHint'
                      : 'questions.optionsMultipleHint',
                  )}
                </p>

                {body.options.map((option, index) => (
                  <div key={index} className="nv-option-row">
                    <TextInput
                      aria-label={t('questions.optionText')}
                      placeholder={t('questions.optionText')}
                      value={option.text}
                      onChange={(e) =>
                        patch({
                          options: body.options.map((o, i) => (i === index ? { ...o, text: e.target.value } : o)),
                        })
                      }
                    />
                    <TextInput
                      aria-label={t('questions.optionScore')}
                      type="number"
                      min={0}
                      max={100}
                      className="nv-option-score"
                      value={option.score}
                      onChange={(e) =>
                        patch({
                          options: body.options.map((o, i) =>
                            i === index ? { ...o, score: Number(e.target.value) } : o,
                          ),
                        })
                      }
                    />
                    <IconButton
                      label={t('common.delete')}
                      icon={IconTrash2}
                      tone="danger"
                      onClick={() => patch({ options: body.options.filter((_, i) => i !== index) })}
                    />
                  </div>
                ))}

                <Button
                  variant="ghost"
                  icon={IconPlus}
                  className="nv-self-start"
                  onClick={() => patch({ options: [...body.options, { text: '', score: 100 }] })}
                >
                  {t('questions.addOption')}
                </Button>

                {body.options.length < 2 ? (
                  // Said before saving rather than after: the server refuses it at publish
                  // and that is a long way from here.
                  <p className="nv-hint">{t('questions.needTwoOptions')}</p>
                ) : null}
              </div>
            ) : null}

            <Field label={t('questions.text')}>
              {({ id }) => (
                <Textarea
                  id={id}
                  placeholder={t('questions.textPlaceholder')}
                  value={body.text}
                  onChange={(e) => patch({ text: e.target.value })}
                />
              )}
            </Field>

            <Field label={t('questions.guidance')}>
              {({ id }) => (
                <Textarea
                  id={id}
                  placeholder={t('questions.guidancePlaceholder')}
                  value={body.guidance ?? ''}
                  onChange={(e) => patch({ guidance: e.target.value || null })}
                />
              )}
            </Field>

            <div className="nv-drawer__section">
              <h3 className="nv-h3">{t('questions.scoring')}</h3>
              <p className="nv-hint">{t('questions.scoringHint')}</p>

              <Field label={t('questions.critical')}>
                {({ id }) => (
                  <Select
                    id={id}
                    value={body.criticalScope}
                    disabled={!isScored(body.answerType)}
                    onChange={(e) => patch({ criticalScope: e.target.value as CriticalScope })}
                  >
                    {CRITICAL_SCOPES.map((value) => (
                      <option key={value} value={value}>{t(`critical.${value}`)}</option>
                    ))}
                  </Select>
                )}
              </Field>

              <div className="nv-form-pair">
                <Field label={t('questions.commentRequirement')}>
                  {({ id }) => (
                    <Select
                      id={id}
                      value={body.commentRequirement}
                      onChange={(e) => patch({ commentRequirement: e.target.value as RequirementTrigger })}
                    >
                      {REQUIREMENT_TRIGGERS.filter((v) => isScored(body.answerType) || v !== 'WhenNegative').map((value) => (
                        <option key={value} value={value}>{t(`trigger.${value}`)}</option>
                      ))}
                    </Select>
                  )}
                </Field>

                <Field label={t('questions.photoRequirement')}>
                  {({ id }) => (
                    <Select
                      id={id}
                      value={body.photoRequirement}
                      onChange={(e) => patch({ photoRequirement: e.target.value as RequirementTrigger })}
                    >
                      {REQUIREMENT_TRIGGERS.filter((v) => isScored(body.answerType) || v !== 'WhenNegative').map((value) => (
                        <option key={value} value={value}>{t(`trigger.${value}`)}</option>
                      ))}
                    </Select>
                  )}
                </Field>
              </div>

              <Toggle
                checked={body.allowsNotApplicable}
                onCheckedChange={(allowsNotApplicable) => patch({ allowsNotApplicable })}
                label={t('questions.allowNotApplicable')}
              />

              {body.allowsNotApplicable ? (
                // The rule, not a choice (ADR-022). There used to be a choice here, and under
                // points the form overrode it — the test team picked one and saw the other on
                // the day. Saying where the points go is what that report was missing.
                <p className="nv-subtle">{t('questions.notApplicableRule')}</p>
              ) : null}
            </div>

            {criticalAndSkippable ? <Alert>{t('validation.criticalAndSkippable')}</Alert> : null}
            {formError ? <Alert>{formError}</Alert> : null}
          </>
        ) : null}
      </Drawer>

      <ConfirmDialog
        open={doomed !== null}
        title={doomed ? t('common.deleteTitle', { name: doomed.code }) : ''}
        confirmLabel={t('common.delete')}
        onConfirm={() => (doomed ? act(() => api.questions.remove(doomed.id)) : Promise.resolve())}
        onClose={() => setDoomed(null)}
      >
        {doomed ? (
          <>
            <p>{doomed.text}</p>
            {doomed.usedInTemplates > 0 ? (
              <p className="nv-row">
                <IconTriangleAlert size={16} />
                {tn('questions.inUseWarning', doomed.usedInTemplates)}
              </p>
            ) : (
              <p>{t('common.deleteWarning')}</p>
            )}
          </>
        ) : null}
      </ConfirmDialog>
    </>
  );
}

